import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { SocialAuthButton } from '@/components/auth/SocialAuthButton';
import { AuthDivider } from '@/components/auth/AuthDivider';
import { PortalBranding, getPortalConfig, type PortalType } from '@/components/auth/PortalBranding';
import { Loader2, Heart, AlertCircle, CheckCircle, ArrowLeft, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const passwordRequirements = [
  { label: 'At least 6 characters', test: (p: string) => p.length >= 6 },
  { label: 'Contains a number', test: (p: string) => /\d/.test(p) },
];

export default function Signup() {
  const [searchParams] = useSearchParams();
  const portal = (searchParams.get('portal') as PortalType) || 'patient';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { signUp, user, isLoading: authLoading, isAdmin, isDoctor } = useAuth();
  const navigate = useNavigate();
  const portalConfig = getPortalConfig(portal);
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);

  // If no email confirmation needed, AuthContext auto-logs the user in after signUp.
  // Wait for roles to load (authLoading=false) then redirect.
  useEffect(() => {
    if (authLoading || !user || needsEmailConfirmation) return;
    if (isAdmin) { navigate('/admin', { replace: true }); return; }
    if (isDoctor) { navigate('/consultation', { replace: true }); return; }
    navigate('/dashboard', { replace: true });
  }, [user, authLoading, isAdmin, isDoctor, needsEmailConfirmation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    if (!/\d/.test(password)) {
      setError('Password must contain at least one number');
      setIsLoading(false);
      return;
    }

    try {
      const { error, needsEmailConfirmation: confirmRequired } = await signUp(email, password, fullName);
      if (error) {
        const msg = error.message;
        if (msg.includes('already registered') || msg.includes('User already registered')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else if (msg.includes('Password should be')) {
          setError('Password must be at least 6 characters and contain a number.');
        } else {
          setError(msg);
        }
      } else if (confirmRequired) {
        // Supabase email confirmation is enabled — user must verify before logging in
        setNeedsEmailConfirmation(true);
        setSuccess(true);
      } else {
        // No email confirmation — user is already logged in, useEffect handles redirect
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-float-delayed" />
      
      <motion.div 
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to home
        </Link>

        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <motion.div 
            className="relative"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Heart className="h-10 w-10 text-primary" />
            <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full" />
          </motion.div>
          <span className="text-2xl font-bold text-foreground">
            Apne<span className="text-primary">Doctors</span>
          </span>
        </div>

        <Card className="border-border/30 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden">
          {/* Gradient Top Border */}
          <div className="h-1 bg-gradient-to-r from-primary via-secondary to-primary" />
          
          <CardHeader className="space-y-4 pt-8">
            <PortalBranding portal={portal} />
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 px-6">
              {/* Error Alert */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </motion.div>
              )}
              
              {/* Success Alert */}
              {success && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Alert className="border-green-500/30 bg-green-500/10">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <AlertDescription className="text-green-500">
                      {needsEmailConfirmation
                        ? `Account created! We sent a confirmation link to ${email}. Click it to activate your account.`
                        : 'Account created! Taking you to your dashboard…'}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {/* Google Sign Up */}
              <SocialAuthButton provider="google" disabled={isLoading || success} />
              
              <AuthDivider />
              
              {/* Full Name Field */}
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium">
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder={portal === 'doctor' ? 'Dr. John Smith' : 'John Smith'}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={isLoading || success}
                  className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20 transition-all"
                />
              </div>
              
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading || success}
                  className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20 transition-all"
                />
              </div>
              
              {/* Password Field */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading || success}
                  className="bg-background/50 border-border/50 focus:border-primary focus:ring-primary/20"
                />
                {/* Password Requirements */}
                <div className="space-y-1 pt-1">
                  {passwordRequirements.map((req, i) => (
                    <div 
                      key={i}
                      className={cn(
                        'flex items-center gap-2 text-xs transition-colors',
                        req.test(password) ? 'text-green-500' : 'text-muted-foreground'
                      )}
                    >
                      <Check className={cn(
                        'h-3 w-3 transition-opacity',
                        req.test(password) ? 'opacity-100' : 'opacity-30'
                      )} />
                      {req.label}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col gap-4 px-6 pb-8">
              <Button 
                type="submit" 
                className="w-full h-12 text-base font-semibold relative overflow-hidden group"
                disabled={isLoading || success}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-primary/0 via-white/20 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  portalConfig.signupCta
                )}
              </Button>
              
              <p className="text-sm text-muted-foreground text-center">
                Already have an account?{' '}
                <Link 
                  to={`/login?portal=${portal}`} 
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Sign in
                </Link>
              </p>

              {/* Portal Switcher */}
              {portal === 'patient' ? (
                <p className="text-xs text-muted-foreground text-center">
                  Are you a doctor?{' '}
                  <Link to="/doctor/signup" className="text-primary hover:underline font-medium">
                    Apply for access
                  </Link>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground text-center">
                  Not a {portal}?{' '}
                  <Link to="/signup" className="text-primary hover:underline">
                    Patient signup
                  </Link>
                </p>
              )}
            </CardFooter>
          </form>
        </Card>

        {/* Footer */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
        </p>
      </motion.div>
    </div>
  );
}
